import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Grid, LinearProgress, Button } from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import EventNoteIcon from "@mui/icons-material/EventNote";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import AcademicCountdown from "../../DynamicComponent/AcademicCountdown";

function StudentDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadDashboard = useCallback(async () => {
    try {
      const applicationId = sessionStorage.getItem("applicationId") || "";
      const url = applicationId
        ? `student/dashboard?applicationId=${applicationId}`
        : "student/dashboard";
      const response = await fetchData(url);
      if (response?.data) {
        setDashboardData(response.data);
      }
    } catch (error) {
      // silently fall through to default values
    }

    try {
      const attRes = await fetchData("student/attendance/monthly");
      if (attRes?.data && Array.isArray(attRes.data)) {
        setAttendanceData(attRes.data.map((v, i) => ({ month: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i], value: v })));
      } else if (attRes?.data) {
        setAttendanceData(Object.entries(attRes.data).map(([month, value]) => ({ month, value })));
      }
    } catch {
      setAttendanceData([
        {month:"Jan",value:80},{month:"Feb",value:75},{month:"Mar",value:90},
        {month:"Apr",value:85},{month:"May",value:88},{month:"Jun",value:70},
        {month:"Jul",value:82},{month:"Aug",value:92},{month:"Sep",value:87},
        {month:"Oct",value:78},{month:"Nov",value:84},{month:"Dec",value:80}
      ]);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleToastClose = () => {
    setToast({ ...toast, open: false });
  };

  const kpiCards = [
    { label: "Total Subjects", value: dashboardData?.totalSubjects || 1, color: "#244C77", icon: <SchoolIcon /> },
    { label: "Attendance", value: dashboardData?.attendance || "72%", color: "#4caf50", icon: <CheckCircleIcon /> },
    { label: "Upcoming Exams", value: dashboardData?.upcomingExams || 5, color: "#ff9800", icon: <EventNoteIcon /> },
    { label: "Notification", value: dashboardData?.notifications || 4, color: "#2196f3", icon: <NotificationsIcon /> },
  ];

  const upcomingProjects = dashboardData?.upcomingProjects || [];

  const schedule = dashboardData?.schedule || [];

  const styles = {
    container: { padding: "1.5rem" },
    welcomeText: { fontSize: "28px", fontWeight: "bold", margin: 0, color: "#1a1a2e" },
    welcomeSpan: {
      background: "linear-gradient(90deg, #4A7BA8, #244C77)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    kpiCard: {
      padding: "20px",
      borderRadius: "16px",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    },
    kpiIcon: (color) => ({
      width: "50px",
      height: "50px",
      borderRadius: "12px",
      backgroundColor: color + "20",
      color: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    profileCard: {
      padding: "24px",
      borderRadius: "16px",
      backgroundColor: "#fff",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
      textAlign: "center",
    },
    profileImage: {
      width: "80px",
      height: "80px",
      borderRadius: "50%",
      backgroundColor: "#e0d6ff",
      margin: "0 auto 12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "32px",
      color: "#244C77",
    },
    sectionCard: {
      padding: "20px",
      borderRadius: "16px",
      backgroundColor: "#fff",
      boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    },
    chartBar: (height, color) => ({
      width: "24px",
      height: `${height}%`,
      backgroundColor: color,
      borderRadius: "4px 4px 0 0",
      minHeight: "8px",
    }),
    projectItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: "1px solid #f0f0f0",
    },
    scheduleItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "10px 0",
      borderBottom: "1px solid #f0f0f0",
    },
  };

  return (
    <Box sx={styles.container}>
      <Toast open={toast.open} handleClose={handleToastClose} severity={toast.severity} message={toast.message} />
      <AcademicCountdown />

      <Typography sx={styles.welcomeText}>
        Welcome to <span style={styles.welcomeSpan}>Cummings!</span>
      </Typography>
      <Typography sx={{ color: "#8F95B2", fontSize: "14px", mb: 3 }}>
        Track your academic progress and stay updated
      </Typography>

      <Grid container spacing={3}>
        {/* KPI Cards */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {kpiCards.map((card, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Paper elevation={0} sx={styles.kpiCard}>
                  <Box sx={styles.kpiIcon(card.color)}>{card.icon}</Box>
                  <Box>
                    <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" }}>
                      {card.value}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#8F95B2" }}>{card.label}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Attendance Chart */}
          <Paper elevation={0} sx={{ ...styles.sectionCard, mt: 3 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 2 }}>Attendance Overview</Typography>
            <Box sx={{ display: "flex", alignItems: "flex-end", gap: "12px", height: "180px", justifyContent: "space-between" }}>
              {attendanceData.map((item, index) => (
                <Box key={index} sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                  <Box sx={styles.chartBar(item.value, index % 2 === 0 ? "#244C77" : "#4A7BA8")} />
                  <Typography sx={{ fontSize: "11px", color: "#8F95B2", mt: 1 }}>{item.month}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>

          {/* Performance */}
          <Paper elevation={0} sx={{ ...styles.sectionCard, mt: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Performance</Typography>
              <Typography sx={{ fontSize: "14px", color: "#4caf50", fontWeight: "bold" }}>On track</Typography>
            </Box>
            <Typography sx={{ fontSize: "28px", fontWeight: "bold", color: "#244C77", mb: 1 }}>
              {dashboardData?.performance || 87}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={dashboardData?.performance || 87}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "#e0d6ff",
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #4A7BA8, #244C77)",
                  borderRadius: 5,
                },
              }}
            />
          </Paper>
        </Grid>

        {/* Right Column - Profile & Schedule */}
        <Grid item xs={12} md={3}>
          {/* Student Profile Card */}
          <Paper elevation={0} sx={styles.profileCard}>
            <Box sx={styles.profileImage}>AB</Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
              {dashboardData?.studentName || "Andrei Bloom"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", mt: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: "14px", color: "#8F95B2" }} />
              <Typography sx={{ fontSize: "12px", color: "#8F95B2" }}>
                {dashboardData?.location || "Chennai, India"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-around", mt: 2, pt: 2, borderTop: "1px solid #f0f0f0" }}>
              <Box>
                <Typography sx={{ fontWeight: "bold", fontSize: "18px", color: "#244C77" }}>28</Typography>
                <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>Projects</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: "bold", fontSize: "18px", color: "#ff9800" }}>60</Typography>
                <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>Assignments</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontWeight: "bold", fontSize: "18px", color: "#4caf50" }}>2</Typography>
                <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>Grade</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Upcoming Projects */}
          <Paper elevation={0} sx={{ ...styles.sectionCard, mt: 3 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Upcoming Projects</Typography>
            {upcomingProjects.map((project, index) => (
              <Box key={index} sx={styles.projectItem}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: project.color }} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{project.title}</Typography>
                </Box>
                <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>{project.date}</Typography>
              </Box>
            ))}
          </Paper>

          {/* ── Gap #18: Mandatory Leave Form — standing link per FRD §19.1.7 ── */}
          <Paper elevation={0} sx={{ ...styles.sectionCard, mt: 3, borderLeft: "4px solid #244C77", backgroundColor: "#faf9ff" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <AssignmentReturnIcon sx={{ color: "#244C77", fontSize: "20px" }} />
              <Typography sx={{ fontWeight: "bold", fontSize: "15px", color: "#1a1a2e" }}>Mandatory Leave Form</Typography>
            </Box>
            <Typography sx={{ fontSize: "12px", color: "#8F95B2", mb: 1.5 }}>
              Submit a voluntary notice for planned absences or extended medical leave.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="small"
              onClick={() => { window.location.href = "/dashboard/leave-management"; }}
              sx={{
                background: "linear-gradient(135deg, #244C77, #4A7BA8)",
                color: "#fff",
                textTransform: "none",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "bold",
                "&:hover": { background: "linear-gradient(135deg, #244C77, #4A7BA8)" },
              }}
            >
              Submit Leave Form →
            </Button>
          </Paper>

          {/* Schedule */}
          <Paper elevation={0} sx={{ ...styles.sectionCard, mt: 3 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 0.5 }}>Schedule</Typography>
            <Typography sx={{ fontSize: "12px", color: "#244C77", fontWeight: "bold", mb: 1 }}>27 May</Typography>
            {schedule.map((item, index) => (
              <Box key={index} sx={styles.scheduleItem}>
                <AccessTimeIcon sx={{ fontSize: "16px", color: "#8F95B2" }} />
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{item.subject}</Typography>
                  <Typography sx={{ fontSize: "11px", color: "#8F95B2" }}>{item.time}</Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default StudentDashboard;
